import React, { useEffect, useRef, useState } from 'react';
import { Video, Loader2, AlertCircle, Download, Info, Clock, Search, ImageOff, Repeat } from 'lucide-react';
import MobileCard from '../../camera/components/mobileCard/MobileCard';
import MobileButton from '../../camera/components/mobileButton/MobileButton';
import { generateEditorVideo, getMusicTracks, getVideoStatus } from '../api/videoEditor.api';
import { getImageRange } from '../../camera/api/iframeConfig.api';
import { getObraByFeedID } from '../../obras/api/obras.api';
import { STATUS_STYLES, formatDateTime } from '../utils/editorVideoFormat';
import CreatedVideosTab from './CreatedVideosTab';
import EditorVideosCarousel from './EditorVideosCarousel';
import EditorEstimatePanel from './EditorEstimatePanel';
import BasicPresetPicker from './BasicPresetPicker';
import { FieldError, LogoField, MusicField, RecipientsField, fieldLabelClass, fieldInputClass } from './EditorFormFields';
import { getObraTimeZone } from '../utils/obraTimeZone';
import { EDITOR_PRESETS, buildPresetConfig, isPresetAvailable } from '../utils/editorPresets';
import useEditorEstimate from '../hooks/useEditorEstimate';
import useEditorVideos from '../hooks/useEditorVideos';
import useResponsibleRecipients from '../hooks/useResponsibleRecipients';

const POLL_INTERVAL_MS = 5000;
const POLL_TIMEOUT_MS = 2 * 60 * 1000;

const PERIOD_OPTIONS = [
  { value: '24H', label: 'Últimas 24 horas' },
  { value: '7D', label: 'Últimos 7 días' },
  { value: '15D', label: 'Últimos 15 días' },
  { value: '30D', label: 'Últimos 30 días' },
  { value: '90D', label: 'Últimos 90 días' },
  { value: 'COMPLETO', label: 'Completo (todo el historial)' },
  { value: 'CUSTOM', label: 'Personalizado' },
];

const ORIENTATION_OPTIONS = [
  { value: 'LANDSCAPE_16_9', label: 'Horizontal (16:9)' },
  { value: 'PORTRAIT_9_16', label: 'Vertical (9:16)' },
];

const INTERVAL_OPTIONS = [
  { value: '1', label: '1 minuto' },
  { value: '5', label: '5 minutos' },
  { value: '10', label: '10 minutos' },
  { value: '15', label: '15 minutos' },
  { value: '20', label: '20 minutos' },
  { value: '25', label: '25 minutos' },
  { value: '30', label: '30 minutos' },
  { value: '60', label: '1 hora' },
  { value: '120', label: '2 horas' },
  { value: '360', label: '6 horas' },
  { value: '1440', label: '24 horas' },
];

const QUALITY_OPTIONS = [
  { value: 'HD', label: 'HD (720p)' },
  { value: 'FHD', label: 'Full HD (1080p)' },
  { value: 'UHD_4K', label: '4K (UHD)' },
];

const DURATION_OPTIONS = ['5', '10', '15', '30', '60', '90'].map((value) => ({
  value,
  label: `${value} segundos`,
}));

const CADENCE_OPTIONS = [
  { value: '7', label: 'Cada 7 días' },
  { value: '14', label: 'Cada 14 días' },
  { value: '28', label: 'Cada 28 días' },
  { value: '42', label: 'Cada 42 días' },
];

const PREFERRED_HOUR_OPTIONS = Array.from({ length: 13 }, (_, i) => {
  const hour = 8 + i;
  return { value: String(hour), label: `${String(hour).padStart(2, '0')}:00` };
});

const formatDateOnly = (value) => {
  if (!value) return '---';
  return new Date(`${value}T00:00:00`).toLocaleDateString([], { dateStyle: 'medium' });
};

const DEFAULT_CONFIG = {
  period: '24H',
  customStart: '',
  customEnd: '',
  orientation: 'LANDSCAPE_16_9',
  intervalMinutes: '30',
  // Franja horaria deshabilitada temporalmente en la UI, ver bloque comentado en el form.
  // timeOfDayStart: '',
  // timeOfDayEnd: '',
  quality: 'FHD',
  durationSeconds: '15',
  logo: null,
  useClientLogo: false,
  musicTrack: '',
  musicVolume: '',
  scheduleEnabled: false,
  cadenceDays: '7',
  preferredHour: '8',
  recipientUserIds: [],
  temporalBlend: true,
};

const ImageRangePreview = ({ label, image }) => (
  <div className="flex-1 min-w-0">
    <p className="text-xs font-semibold text-gray-600 mb-1.5">{label}</p>
    {image ? (
      <div className="space-y-1 max-w-[220px] mx-auto sm:mx-0">
        <img
          src={image.url}
          alt={label}
          className="w-full aspect-[4/3] object-cover rounded-lg border border-gray-200"
        />
        <p className="text-xs text-gray-500">{formatDateOnly(image.date)}</p>
      </div>
    ) : (
      <div className="max-w-[220px] mx-auto sm:mx-0 aspect-[4/3] flex flex-col items-center justify-center gap-1 rounded-lg border border-dashed border-gray-300 bg-white text-gray-400">
        <ImageOff className="w-5 h-5" />
        <p className="text-xs">Sin imágenes en este rango</p>
      </div>
    )}
  </div>
);

const TABS = [
  { value: 'nuevo', label: 'Nuevo' },
  { value: 'creados', label: 'Listado de creados' },
];

const MODES = [
  { value: 'basic', label: 'Básico' },
  { value: 'advanced', label: 'Avanzado' },
];

const DEFAULT_PRESET_ID = EDITOR_PRESETS[0].id;

const VideoEditor = ({ user }) => {
  const [activeTab, setActiveTab] = useState('nuevo');
  const editorVideos = useEditorVideos(user.feedID);
  const responsibleRecipients = useResponsibleRecipients(user?.customerId);
  const [mode, setMode] = useState('basic');
  const [presetId, setPresetId] = useState(DEFAULT_PRESET_ID);
  const [presetAvailability, setPresetAvailability] = useState({});
  const [obraTimeZone, setObraTimeZone] = useState(null);
  const [obraTimeZoneSettled, setObraTimeZoneSettled] = useState(false);
  const [config, setConfig] = useState(() => ({ ...DEFAULT_CONFIG, ...buildPresetConfig(DEFAULT_PRESET_ID) }));
  const [fieldErrors, setFieldErrors] = useState({});
  const [submitError, setSubmitError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [job, setJob] = useState(null);
  const [slow, setSlow] = useState(false);
  const [musicTracks, setMusicTracks] = useState([]);
  const [musicTracksError, setMusicTracksError] = useState(null);
  const [imageRange, setImageRange] = useState(null);
  const [imageRangeLoading, setImageRangeLoading] = useState(false);
  const [imageRangeError, setImageRangeError] = useState(null);

  const pollIntervalRef = useRef(null);
  const pollTimeoutRef = useRef(null);
  const logoInputRef = useRef(null);

  const clearPolling = () => {
    if (pollIntervalRef.current) clearInterval(pollIntervalRef.current);
    if (pollTimeoutRef.current) clearTimeout(pollTimeoutRef.current);
  };

  useEffect(() => {
    return () => clearPolling();
  }, []);

  useEffect(() => {
    let cancelled = false;
    getMusicTracks()
      .then((tracks) => {
        if (!cancelled) setMusicTracks(tracks || []);
      })
      .catch(() => {
        if (!cancelled) setMusicTracksError('No se pudo cargar el listado de música.');
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // Camera-site time zone for the Basic time window. Fails open: any error or missing location keeps the browser zone.
  useEffect(() => {
    if (!user?.feedID) return undefined;
    let cancelled = false;
    getObraByFeedID(user.feedID)
      .then((obra) => {
        if (!cancelled) setObraTimeZone(getObraTimeZone(obra));
      })
      .catch(() => {})
      .finally(() => {
        if (!cancelled) setObraTimeZoneSettled(true);
      });
    return () => {
      cancelled = true;
    };
  }, [user?.feedID]);

  // Basic presets that reach further back than the camera history are disabled. Fails open on any error.
  useEffect(() => {
    if (!user?.feedID) return undefined;
    let cancelled = false;
    getImageRange(user.feedID, { period: 'COMPLETO' })
      .then((data) => {
        if (cancelled) return;
        const firstDate = data?.firstImage?.date?.slice(0, 10);
        const now = new Date();
        const availability = {};
        EDITOR_PRESETS.forEach((preset) => {
          availability[preset.id] = isPresetAvailable(preset.id, firstDate, now);
        });
        availability.firstDate = firstDate || null; // para "desde el dd/mm" y el tooltip
        setPresetAvailability(availability);
        setPresetId((current) => (availability[current] === false ? 'ALL_TIME' : current));
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [user?.feedID]);

  useEffect(() => {
    setImageRange(null);
    setImageRangeError(null);
  }, [config.period, config.customStart, config.customEnd]);

  const estimateState = useEditorEstimate(config, user?.feedID);

  const startPolling = (jobId) => {
    clearPolling();
    setSlow(false);

    pollIntervalRef.current = setInterval(async () => {
      try {
        const currentJob = await getVideoStatus(jobId);
        setJob(currentJob);
        if (currentJob.status === 'COMPLETED' || currentJob.status === 'FAILED') {
          clearPolling();
        }
      } catch (err) {
        console.error('Error polling editor video status:', err);
        setSubmitError('No se pudo consultar el estado del video. Intentá nuevamente.');
        clearPolling();
      }
    }, POLL_INTERVAL_MS);

    pollTimeoutRef.current = setTimeout(() => {
      setSlow(true);
    }, POLL_TIMEOUT_MS);
  };

  if (!user?.feedID) {
    return (
      <MobileCard className="bg-amber-50 border-amber-200">
        <p className="text-amber-700 text-sm text-center">
          Este proyecto no tiene un Feed ID configurado para generar videos.
        </p>
      </MobileCard>
    );
  }

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setConfig((prev) => {
      const next = { ...prev, [name]: type === 'checkbox' ? checked : value };
      if (name === 'period' && value === '24H' && prev.intervalMinutes === '1440') {
        next.intervalMinutes = DEFAULT_CONFIG.intervalMinutes;
      }
      if (name === 'period' && value !== '24H' && prev.intervalMinutes === '1') {
        next.intervalMinutes = DEFAULT_CONFIG.intervalMinutes;
      }
      return next;
    });
  };

  const toggleRecipient = (id) =>
    setConfig((prev) => ({
      ...prev,
      recipientUserIds: prev.recipientUserIds.includes(id)
        ? prev.recipientUserIds.filter((x) => x !== id)
        : [...prev.recipientUserIds, id],
    }));

  const intervalOptions =
    config.period === '24H'
      ? INTERVAL_OPTIONS.filter((opt) => opt.value !== '1440')
      : INTERVAL_OPTIONS.filter((opt) => opt.value !== '1');

  const customRangeInverted =
    config.period === 'CUSTOM' &&
    config.customStart &&
    config.customEnd &&
    new Date(config.customEnd) < new Date(config.customStart);

  const canFetchImageRange =
    config.period !== 'CUSTOM' ||
    (Boolean(config.customStart) && Boolean(config.customEnd) && !customRangeInverted);

  const handleFetchImageRange = async () => {
    if (!canFetchImageRange) return;
    setImageRangeLoading(true);
    setImageRangeError(null);
    try {
      const data = await getImageRange(user.feedID, {
        period: config.period,
        customStart: config.period === 'CUSTOM' ? config.customStart.slice(0, 10) : undefined,
        customEnd: config.period === 'CUSTOM' ? config.customEnd.slice(0, 10) : undefined,
      });
      setImageRange(data);
    } catch (err) {
      console.error('Error fetching image range:', err);
      setImageRangeError('No se pudieron obtener las imágenes del período.');
    } finally {
      setImageRangeLoading(false);
    }
  };

  // Presets only own period, duration, blend, interval and time window; logo and music survive mode/preset changes.
  const applyPreset = (id) => setConfig((prev) => ({ ...prev, ...buildPresetConfig(id, new Date(), obraTimeZone), scheduleEnabled: false }));

  const handlePresetSelect = (id) => {
    setPresetId(id);
    applyPreset(id);
  };

  const handleModeChange = (nextMode) => {
    if (nextMode === mode) return;
    setMode(nextMode);
    if (nextMode === 'basic') {
      applyPreset(presetId);
    } else {
      setConfig((prev) => ({
        ...DEFAULT_CONFIG,
        logo: prev.logo,
        useClientLogo: prev.useClientLogo,
        musicTrack: prev.musicTrack,
        musicVolume: prev.musicVolume,
      }));
    }
    setFieldErrors({});
  };

  const handleLogoChange = (e) => {
    const file = e.target.files?.[0] || null;
    // A file, when present, always wins over useClientLogo on the backend,
    // so the UI keeps them mutually exclusive to avoid confusing the user.
    setConfig((prev) => ({ ...prev, logo: file, useClientLogo: file ? false : prev.useClientLogo }));
  };

  const handleClearLogo = () => {
    setConfig((prev) => ({ ...prev, logo: null }));
    if (logoInputRef.current) logoInputRef.current.value = '';
  };

  const validate = (config) => {
    const errors = {};

    if (config.period === 'CUSTOM') {
      if (!config.customStart) errors.customStart = 'La fecha y hora de inicio son obligatorias para un período personalizado.';
      if (!config.customEnd) errors.customEnd = 'La fecha y hora de fin son obligatorias para un período personalizado.';
      if (config.customStart && config.customEnd && new Date(config.customEnd) < new Date(config.customStart)) {
        errors.customEnd = 'La fecha de fin debe ser igual o posterior a la fecha de inicio.';
      }
    }

    // Franja horaria (timeOfDayStart/timeOfDayEnd) deshabilitada temporalmente en la UI,
    // se deja de mandar al backend. Validación comentada junto con el resto de este campo.
    // if (config.timeOfDayStart && config.timeOfDayEnd && config.timeOfDayEnd <= config.timeOfDayStart) {
    //   errors.timeOfDayEnd = 'La hora de fin debe ser posterior a la hora de inicio.';
    // }

    if (config.musicVolume !== '' && !config.musicTrack) {
      errors.musicVolume = 'musicVolume requiere que musicTrack esté configurado.';
    }
    if (config.musicVolume !== '' && (Number(config.musicVolume) < 0 || Number(config.musicVolume) > 100)) {
      errors.musicVolume = 'El volumen debe estar entre 0 y 100.';
    }

    return errors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Basic recomputes its range at submit time so "hasta ahora" is not stale.
    const submitConfig = mode === 'basic' ? { ...config, ...buildPresetConfig(presetId, new Date(), obraTimeZone) } : config;
    const validationErrors = validate(submitConfig);
    setFieldErrors(validationErrors);
    setSubmitError(null);
    if (Object.keys(validationErrors).length > 0) return;

    clearPolling();
    setSlow(false);
    setJob(null);
    setSubmitting(true);

    try {
      const formData = new FormData();
      formData.append('feedId', user.feedID);
      formData.append('period', submitConfig.period);
      if (submitConfig.period === 'CUSTOM') {
        formData.append('customStart', new Date(submitConfig.customStart).toISOString());
        formData.append('customEnd', new Date(submitConfig.customEnd).toISOString());
      }
      formData.append('orientation', submitConfig.orientation);
      formData.append('intervalMinutes', String(submitConfig.intervalMinutes));
      formData.append('quality', submitConfig.quality);
      formData.append('durationSeconds', String(submitConfig.durationSeconds));
      // Franja horaria: el modo Avanzado la tiene deshabilitada en la UI (no se manda); solo la usan los presets del modo Básico.
      if (submitConfig.timeOfDayStart) formData.append('timeOfDayStart', submitConfig.timeOfDayStart);
      if (submitConfig.timeOfDayEnd) formData.append('timeOfDayEnd', submitConfig.timeOfDayEnd);
      // La franja se interpreta en la zona horaria del navegador (solo se manda junto con la franja).
      if (submitConfig.timeZone) formData.append('timeZone', submitConfig.timeZone);
      formData.append('temporalBlend', String(submitConfig.temporalBlend));
      // useClientLogo is ignored by the backend whenever a file is sent, but we
      // still send an explicit 'false' so the field is always present as a string.
      formData.append('useClientLogo', submitConfig.logo ? 'false' : String(submitConfig.useClientLogo));
      if (submitConfig.logo) formData.append('logo', submitConfig.logo);
      if (submitConfig.musicTrack) formData.append('musicTrack', submitConfig.musicTrack);
      if (submitConfig.musicVolume !== '') formData.append('musicVolume', String(submitConfig.musicVolume));
      if (submitConfig.scheduleEnabled) {
        formData.append('cadenceDays', String(submitConfig.cadenceDays));
        formData.append('preferredHour', String(submitConfig.preferredHour));
        // Recipients only apply to scheduled videos: the backend ignores them without a cadence.
        submitConfig.recipientUserIds.forEach((id) => formData.append('recipientUserIds', id));
      }

      const data = await generateEditorVideo(formData);
      setJob(data);
      if (data?.id && data.status !== 'COMPLETED' && data.status !== 'FAILED') {
        startPolling(data.id);
      }
    } catch (err) {
      console.error('Error generating editor video:', err);
      const responseData = err?.response?.data;
      if (responseData?.errors && typeof responseData.errors === 'object') {
        setFieldErrors(responseData.errors);
        setSubmitError(responseData.message || 'Revisá los campos marcados.');
      } else if (typeof responseData?.error === 'string') {
        setSubmitError(responseData.error);
      } else {
        setSubmitError('No se pudo iniciar la generación del video. Intentá nuevamente.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleGenerateAnother = () => {
    clearPolling();
    setSlow(false);
    setJob(null);
    setSubmitError(null);
  };

  const isJobActive = job && (job.status === 'PENDING' || job.status === 'PROCESSING');

  const logoField = (
    <LogoField
      config={config}
      fieldErrors={fieldErrors}
      submitting={submitting}
      logoInputRef={logoInputRef}
      onInputChange={handleInputChange}
      onLogoChange={handleLogoChange}
      onClearLogo={handleClearLogo}
    />
  );

  const musicField = (
    <MusicField
      config={config}
      fieldErrors={fieldErrors}
      submitting={submitting}
      musicTracks={musicTracks}
      musicTracksError={musicTracksError}
      onInputChange={handleInputChange}
    />
  );

  return (
    <div className="space-y-4 md:space-y-6 animate-fadeIn">
      <MobileCard className="bg-gradient-to-br from-blue-50 to-indigo-50 border-blue-200" padding="p-4 md:p-6">
        <div className="flex items-center gap-4">
          <div className="flex-shrink-0 w-12 h-12 md:w-14 md:h-14 rounded-2xl bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center shadow-md">
            <Video className="w-6 h-6 md:w-7 md:h-7 text-white" />
          </div>
          <div className="min-w-0">
            <h2 className="text-lg md:text-2xl font-bold text-gray-800 truncate">Editor de Video</h2>
            <p className="text-xs md:text-sm text-gray-600 truncate">
              Cámara: <span className="font-semibold">{user?.Name || user?.projectName || user.feedID}</span>
            </p>
          </div>
        </div>
      </MobileCard>

      <div className="flex gap-1 p-1 bg-gray-100 rounded-xl w-full sm:w-fit">
        {TABS.map((tab) => (
          <button
            key={tab.value}
            type="button"
            onClick={() => setActiveTab(tab.value)}
            className={`flex-1 sm:flex-none px-4 py-2 text-sm font-semibold rounded-lg transition-colors ${
              activeTab === tab.value
                ? 'bg-white text-gray-800 shadow-sm'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      <EditorVideosCarousel jobs={editorVideos.jobs} onViewAll={() => setActiveTab('creados')} />

      {activeTab === 'creados' && (
        <CreatedVideosTab
          ftpUser={user.feedID}
          jobs={editorVideos.jobs}
          loading={editorVideos.loading}
          error={editorVideos.error}
          onRefresh={editorVideos.refresh}
        />
      )}

      {activeTab === 'nuevo' && (
        <>
      <MobileCard padding="p-4 md:p-6">
        <form className="space-y-5 md:space-y-6" onSubmit={handleSubmit}>
          <div className="flex gap-1 p-1 bg-gray-100 rounded-xl w-full sm:w-fit">
            {MODES.map((m) => (
              <button
                key={m.value}
                type="button"
                onClick={() => handleModeChange(m.value)}
                disabled={submitting}
                className={`flex-1 sm:flex-none px-4 py-2 text-sm font-semibold rounded-lg transition-colors ${
                  mode === m.value
                    ? 'bg-white text-gray-800 shadow-sm'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                {m.label}
              </button>
            ))}
          </div>

          {mode === 'basic' && (
            <>
              <BasicPresetPicker
                selectedId={presetId}
                availability={presetAvailability}
                submitting={submitting}
                onSelect={handlePresetSelect}
              />
              {logoField}
              {musicField}
            </>
          )}

          {mode === 'advanced' && (
            <>
          <div>
            <label className={fieldLabelClass}>Período</label>
            <select
              name="period"
              value={config.period}
              onChange={handleInputChange}
              className={fieldInputClass}
              disabled={submitting}
            >
              {PERIOD_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
            <FieldError message={fieldErrors.period} />
          </div>

          {config.period === 'CUSTOM' && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 p-4 bg-gray-50 rounded-xl border border-gray-200 animate-fadeIn">
              <div>
                <label className={fieldLabelClass}>Desde</label>
                <input
                  type="datetime-local"
                  name="customStart"
                  value={config.customStart}
                  onChange={handleInputChange}
                  className={fieldInputClass}
                  disabled={submitting}
                />
                <FieldError message={fieldErrors.customStart} />
              </div>
              <div>
                <label className={fieldLabelClass}>Hasta</label>
                <input
                  type="datetime-local"
                  name="customEnd"
                  value={config.customEnd}
                  onChange={handleInputChange}
                  className={fieldInputClass}
                  disabled={submitting}
                />
                <FieldError message={fieldErrors.customEnd} />
              </div>
            </div>
          )}

          <div className="p-4 bg-gray-50 rounded-xl border border-gray-200 space-y-3">
            <button
              type="button"
              onClick={handleFetchImageRange}
              disabled={submitting || imageRangeLoading || !canFetchImageRange}
              className="flex items-center justify-center gap-2 w-full sm:w-auto px-4 py-2.5 bg-white border border-gray-300 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed text-sm font-semibold text-gray-700 rounded-lg transition-colors"
            >
              {imageRangeLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
              Buscar imagen de principio y fin del período
            </button>
            <p className="text-xs text-gray-500">
              Las imágenes son aproximadas según la disponibilidad de fotos en el período elegido.
            </p>

            {customRangeInverted && (
              <p className="text-xs text-red-600">
                La fecha "Hasta" debe ser igual o posterior a la fecha "Desde".
              </p>
            )}

            {imageRangeError && <p className="text-xs text-red-600">{imageRangeError}</p>}

            {imageRange && (
              <div className="flex flex-col sm:flex-row gap-4 animate-fadeIn">
                <ImageRangePreview label="Primera imagen" image={imageRange.firstImage} />
                <ImageRangePreview label="Última imagen" image={imageRange.lastImage} />
              </div>
            )}
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className={fieldLabelClass}>Orientación</label>
              <select
                name="orientation"
                value={config.orientation}
                onChange={handleInputChange}
                className={fieldInputClass}
                disabled={submitting}
              >
                {ORIENTATION_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
              <FieldError message={fieldErrors.orientation} />
            </div>

            <div>
              <label className={fieldLabelClass}>Intervalo entre fotos</label>
              <select
                name="intervalMinutes"
                value={config.intervalMinutes}
                onChange={handleInputChange}
                className={fieldInputClass}
                disabled={submitting}
              >
                {intervalOptions.map((opt) => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
              <FieldError message={fieldErrors.intervalMinutes} />
            </div>
          </div>

          {/* Franja horaria (timeOfDayStart/timeOfDayEnd) deshabilitada temporalmente en la UI, se pasa vacía.
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 p-4 bg-gray-50 rounded-xl border border-gray-200">
            <div>
              <label className={fieldLabelClass}>Franja horaria - Desde (opcional)</label>
              <input
                type="time"
                name="timeOfDayStart"
                value={config.timeOfDayStart}
                onChange={handleInputChange}
                className={fieldInputClass}
                disabled={submitting}
              />
              <FieldError message={fieldErrors.timeOfDayStart} />
            </div>
            <div>
              <label className={fieldLabelClass}>Franja horaria - Hasta (opcional)</label>
              <input
                type="time"
                name="timeOfDayEnd"
                value={config.timeOfDayEnd}
                onChange={handleInputChange}
                className={fieldInputClass}
                disabled={submitting}
              />
              <FieldError message={fieldErrors.timeOfDayEnd} />
              <p className="mt-1 text-xs text-gray-500">
                Restringe las fotos a un rango horario dentro de cada día (por ej. 8 a 20hs). No se admiten rangos que crucen la medianoche.
              </p>
            </div>
          </div>
          */}

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className={fieldLabelClass}>Calidad</label>
              <select
                name="quality"
                value={config.quality}
                onChange={handleInputChange}
                className={fieldInputClass}
                disabled={submitting}
              >
                {QUALITY_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
              <FieldError message={fieldErrors.quality} />
            </div>

            <div>
              <label className={fieldLabelClass}>Duración del video</label>
              <select
                name="durationSeconds"
                value={config.durationSeconds}
                onChange={handleInputChange}
                className={fieldInputClass}
                disabled={submitting}
              >
                {DURATION_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
              <FieldError message={fieldErrors.durationSeconds} />
            </div>
          </div>

          <div className="p-4 bg-gray-50 rounded-xl border border-gray-200 space-y-1">
            <label className="flex items-center gap-2 text-sm text-gray-700">
              <input
                type="checkbox"
                name="temporalBlend"
                checked={config.temporalBlend}
                onChange={handleInputChange}
                disabled={submitting}
                className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              />
              Suavizar la transición entre fotos
            </label>
            <p className="text-xs text-gray-500">
              Mezcla fotos consecutivas para un movimiento más fluido. Si lo desactivás, cada foto se ve nítida pero con cortes más marcados.
            </p>
          </div>

          <EditorEstimatePanel
            estimate={estimateState.estimate}
            loading={estimateState.loading}
            error={estimateState.error}
            stale={estimateState.stale}
            canGenerate={estimateState.canGenerate}
            onGenerate={estimateState.generate}
            onCancel={estimateState.cancel}
            requestedDurationSeconds={Number(config.durationSeconds)}
          />
              {logoField}
              {musicField}

          <div className="p-4 bg-indigo-50 border border-indigo-200 rounded-xl space-y-3">
            <label className="flex items-center gap-2 text-sm font-semibold text-indigo-900">
              <input
                type="checkbox"
                name="scheduleEnabled"
                checked={config.scheduleEnabled}
                onChange={handleInputChange}
                disabled={submitting}
                className="w-4 h-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
              />
              <Repeat className="w-4 h-4 text-indigo-600" />
              Programar generación automática
            </label>
            <p className="text-xs text-indigo-800">
              Además de generar este video ahora, se crea una programación que repite esta misma configuración cada cierta cantidad de días.
            </p>

            {config.scheduleEnabled && (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-1 animate-fadeIn">
                <div>
                  <label className={fieldLabelClass}>Frecuencia</label>
                  <select
                    name="cadenceDays"
                    value={config.cadenceDays}
                    onChange={handleInputChange}
                    className={fieldInputClass}
                    disabled={submitting}
                  >
                    {CADENCE_OPTIONS.map((opt) => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>
                  <FieldError message={fieldErrors.cadenceDays} />
                </div>
                <div>
                  <label className={fieldLabelClass}>Hora preferida</label>
                  <select
                    name="preferredHour"
                    value={config.preferredHour}
                    onChange={handleInputChange}
                    className={fieldInputClass}
                    disabled={submitting}
                  >
                    {PREFERRED_HOUR_OPTIONS.map((opt) => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>
                  <FieldError message={fieldErrors.preferredHour} />
                </div>
                <RecipientsField
                  recipients={responsibleRecipients.recipients}
                  selectedIds={config.recipientUserIds}
                  loading={responsibleRecipients.loading}
                  error={responsibleRecipients.error}
                  submitting={submitting}
                  fieldError={fieldErrors.recipientUserIds}
                  onToggle={toggleRecipient}
                />
              </div>
            )}
          </div>
            </>
          )}

          {mode === 'advanced' && (
          <div className="p-4 bg-blue-50 border border-blue-200 rounded-xl space-y-2">
            <div className="flex items-center gap-2">
              <Info className="w-4 h-4 text-blue-600 flex-shrink-0" />
              <span className="text-sm font-semibold text-blue-800">Antes de generar</span>
            </div>
            <ul className="space-y-1.5">
              <li className="flex items-start gap-2 text-xs md:text-sm text-blue-800">
                <span className="mt-1.5 w-1 h-1 rounded-full bg-blue-500 flex-shrink-0" />
                <span>Con ciertas combinaciones de período, franja horaria e intervalo puede haber pocas fotos disponibles: el video final puede durar menos que la duración elegida. Esto no es un error.</span>
              </li>
              <li className="flex items-start gap-2 text-xs md:text-sm text-blue-800">
                <span className="mt-1.5 w-1 h-1 rounded-full bg-blue-500 flex-shrink-0" />
                <span>Los períodos "Completo" o personalizados largos no tienen límite y pueden tardar más en procesarse.</span>
              </li>
            </ul>
          </div>
          )}

          {submitError && (
            <div className="flex items-start gap-3 p-4 bg-red-50 border border-red-200 rounded-xl">
              <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-red-800">{submitError}</p>
            </div>
          )}

          <MobileButton
            type="submit"
            variant="primary"
            size="lg"
            fullWidth
            loading={submitting}
            disabled={submitting || Boolean(isJobActive) || (mode === 'basic' && !obraTimeZoneSettled)}
            icon={<Video className="w-5 h-5" />}
            className="bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700"
          >
            Generar Video
          </MobileButton>
        </form>
      </MobileCard>

      {job && (
        <MobileCard padding="p-4 md:p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-base font-bold text-gray-800">Estado del video</h3>
            <span className={`text-xs font-bold px-3 py-1 rounded-full ${STATUS_STYLES[job.status] || 'bg-slate-100 text-slate-600'}`}>
              {job.status}
            </span>
          </div>

          {job.scheduleId && (
            <div className="flex items-start gap-2 text-xs md:text-sm text-indigo-800 bg-indigo-50 border border-indigo-200 rounded-lg px-3 py-2 mb-4">
              <Repeat className="w-4 h-4 flex-shrink-0 mt-0.5" />
              <span>Se creó la programación automática (ID: {job.scheduleId}).</span>
            </div>
          )}

          {isJobActive && (
            <div className="flex flex-col items-center text-center py-6 gap-3">
              <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
              <p className="text-sm text-gray-600">
                Generando el video{job.createdAt ? ` (iniciado ${formatDateTime(job.createdAt)})` : ''}...
              </p>
              <p className="text-xs text-gray-500 max-w-sm">
                Los períodos "Completo" o los rangos personalizados largos no tienen límite y pueden demorar más en procesarse. Esto es esperado, no un error.
              </p>
              {slow && (
                <div className="flex items-start gap-2 text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 max-w-sm">
                  <Clock className="w-4 h-4 flex-shrink-0 mt-0.5" />
                  <span>Está tardando más de lo normal. Es esperable para períodos largos, podés seguir esperando.</span>
                </div>
              )}
            </div>
          )}

          {job.status === 'COMPLETED' && job.videoUrl && (
            <div className="space-y-3">
              <video key={job.videoUrl} src={job.videoUrl} controls className="w-full rounded-lg bg-black" />
              <a
                href={job.videoUrl}
                download
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-center gap-2 w-full px-4 py-2.5 bg-green-600 hover:bg-green-700 text-white text-sm font-semibold rounded-lg transition-colors"
              >
                <Download className="w-4 h-4" />
                Descargar video
              </a>
              <button
                type="button"
                onClick={handleGenerateAnother}
                className="w-full text-sm text-gray-500 hover:text-gray-700 transition-colors"
              >
                Generar otro video
              </button>
            </div>
          )}

          {job.status === 'FAILED' && (
            <div className="space-y-3">
              <div className="flex items-start gap-3 p-4 bg-red-50 border border-red-200 rounded-xl">
                <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-red-800">{job.failureReason || 'Ocurrió un error al generar el video.'}</p>
              </div>
              <button
                type="button"
                onClick={handleGenerateAnother}
                className="w-full text-sm text-gray-500 hover:text-gray-700 transition-colors"
              >
                Intentar de nuevo
              </button>
            </div>
          )}
        </MobileCard>
      )}
        </>
      )}
    </div>
  );
};

export default VideoEditor;
